import { Octokit } from '@octokit/rest'

const octokit = new Octokit()

export interface GitHubProjectVersion {
	name: string
	owner: string
	repo: string
	latestVersion: string | null
	parsedVersion: string | null
}

interface CachedVersions {
	timestamp: number
	versions: GitHubProjectVersion[]
}

type ProjectRef = { owner: string; repo: string; fallbackVersion?: string }

const CACHE_KEY = 'github_versions_cache'
const CACHE_TTL = 60 * 60 * 1000 // 1 hour in milliseconds

function parseVersionFromTag(tag: string): string {
	const patterns = [
		/v?(\d+(\.\d+)*)/i,
		/(\d+(\.\d+)*)/,
		/[^0-9]*(\d+(\.\d+)*)/
	]

	for (const pattern of patterns) {
		const match = tag.match(pattern)
		if (match && match[1]) {
			const parts = match[1].split('.')
			if (parts.length === 1) return `${parts[0]}.0.0`
			if (parts.length === 2) return `${parts[0]}.${parts[1]}.0`
			return match[1]
		}
	}

	return tag
}

function getCachedVersions(): GitHubProjectVersion[] | null {
	if (typeof window === 'undefined') return null

	try {
		const cached = localStorage.getItem(CACHE_KEY)
		if (!cached) return null

		const { timestamp, versions } = JSON.parse(cached) as CachedVersions
		if (Date.now() - timestamp > CACHE_TTL) {
			localStorage.removeItem(CACHE_KEY)
			return null
		}

		return versions
	} catch (error) {
		console.error('Error reading from cache:', error)
		return null
	}
}

function setCachedVersions(versions: GitHubProjectVersion[]): void {
	if (typeof window === 'undefined') return

	try {
		const cache: CachedVersions = {
			timestamp: Date.now(),
			versions
		}
		localStorage.setItem(CACHE_KEY, JSON.stringify(cache))
	} catch (error) {
		console.error('Error writing to cache:', error)
	}
}

function cacheCoversProjects(
	cached: GitHubProjectVersion[],
	projects: ProjectRef[]
): boolean {
	return projects.every(project =>
		cached.some(v => v.owner === project.owner && v.repo === project.repo)
	)
}

function versionFromFallback(project: ProjectRef): GitHubProjectVersion {
	const fallback = project.fallbackVersion ?? null
	return {
		name: `${project.owner}/${project.repo}`,
		owner: project.owner,
		repo: project.repo,
		latestVersion: fallback,
		parsedVersion: fallback ? parseVersionFromTag(fallback) : null
	}
}

async function fetchOneVersion(project: ProjectRef): Promise<GitHubProjectVersion> {
	const { owner, repo, fallbackVersion } = project
	try {
		const response = await octokit.repos.listTags({
			owner,
			repo,
			per_page: 1
		})

		const latestTag = response.data[0]?.name ?? null
		const parsedVersion = latestTag ? parseVersionFromTag(latestTag) : null

		return {
			name: `${owner}/${repo}`,
			owner,
			repo,
			latestVersion: latestTag ?? fallbackVersion ?? null,
			parsedVersion:
				parsedVersion ?? (fallbackVersion ? parseVersionFromTag(fallbackVersion) : null)
		}
	} catch (error) {
		console.error(`Error fetching version for ${owner}/${repo}:`, error)
		return versionFromFallback(project)
	}
}

export async function fetchGitHubProjectVersions(
	projects: ProjectRef[]
): Promise<GitHubProjectVersion[]> {
	const cached = getCachedVersions()
	if (cached && cacheCoversProjects(cached, projects)) {
		return cached
	}

	const byKey = new Map<string, GitHubProjectVersion>()
	if (cached) {
		for (const entry of cached) {
			byKey.set(`${entry.owner}/${entry.repo}`, entry)
		}
	}

	const missing = projects.filter(
		project => !byKey.has(`${project.owner}/${project.repo}`)
	)

	const fetched = await Promise.all(missing.map(fetchOneVersion))
	for (const entry of fetched) {
		byKey.set(`${entry.owner}/${entry.repo}`, entry)
	}

	const results = projects.map(
		project => byKey.get(`${project.owner}/${project.repo}`) ?? versionFromFallback(project)
	)

	setCachedVersions(results)
	return results
}
